import React, { useRef, useEffect, useState } from 'react';
import * as avia from '@cbsinteractive/avia-js';
import { PlayerEvent, getAutoplayCapabilities, createVideoPlayer, removeVideoPlayer, getSystemInfo } from '@cbsinteractive/avia-js';
import { hls } from '@cbsinteractive/avia-js-hls';
import { ui } from '@cbsinteractive/avia-js-ui';
import { gam } from '@cbsinteractive/avia-js-gam';
import { replay } from '@cbsinteractive/avia-js-replay';
import { PLAYLIST, playlist } from '@cbsinteractive/avia-js-playlist';
import 'allsettled-polyfill';
import { UAParser } from 'ua-parser-js';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it;

  if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      return function () {
        if (i >= o.length) return {
          done: true
        };
        return {
          done: false,
          value: o[i++]
        };
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  it = o[Symbol.iterator]();
  return it.next.bind(it);
}

// A type of promise-like that resolves synchronously and supports only one observer

const _iteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.iterator || (Symbol.iterator = Symbol("Symbol.iterator"))) : "@@iterator";

const _asyncIteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.asyncIterator || (Symbol.asyncIterator = Symbol("Symbol.asyncIterator"))) : "@@asyncIterator";

// Asynchronously call a function and send errors to recovery continuation
function _catch(body, recover) {
	try {
		var result = body();
	} catch(e) {
		return recover(e);
	}
	if (result && result.then) {
		return result.then(void 0, recover);
	}
	return result;
}

// Asynchronously await a promise and pass the result to a finally continuation
function _finallyRethrows(body, finalizer) {
	try {
		var result = body();
	} catch (e) {
		return finalizer(true, e);
	}
	if (result && result.then) {
		return result.then(finalizer.bind(null, false), finalizer.bind(null, true));
	}
	return finalizer(false, result);
}

var verifyGlobalVars = function verifyGlobalVars(limit, duration, globalVars) {
  try {
    var attempts = 0;
    return Promise.resolve(new Promise(function check(resolve) {
      attempts += 1;
      var globals = globalVars.map(function (globalVar) {
        return globalVar.split('.');
      });
      var isPresentOnWindow = globals.every(function (globalVar) {
        if (Object.prototype.hasOwnProperty.call(window, globalVar[0])) {
          if (globalVar.length > 1) {
            return globalVar.every(function (prop, i) {
              if (i === 0) {
                return true;
              }

              return Object.prototype.hasOwnProperty.call(eval(globalVar[0]), prop);
            });
          }

          return true;
        }

        return false;
      });

      if (isPresentOnWindow) {
        return resolve(true);
      }

      if (attempts === limit) {
        return resolve(false);
      }

      setTimeout(function () {
        return check(resolve);
      }, duration);
    }));
  } catch (e) {
    return Promise.reject(e);
  }
};
var verifyRefsAreTruthy = function verifyRefsAreTruthy(limit, duration, refs) {
  try {
    var attempts = 0;
    return Promise.resolve(new Promise(function check(resolve) {
      attempts += 1;

      if (refs.every(function (ref) {
        return !!ref.current;
      })) {
        return resolve(true);
      }

      if (attempts === limit) {
        return resolve(false);
      }

      setTimeout(function () {
        return check(resolve);
      }, duration);
    }));
  } catch (e) {
    return Promise.reject(e);
  }
};
var mobileCheck = function mobileCheck() {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    return true;
  }

  return false;
};
var logMessage = function logMessage(msg) {
  var debug = localStorage.getItem('cmvAviaDebug');

  if (debug) {
    for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      rest[_key - 1] = arguments[_key];
    }

    console.log('%cCMV Avia', 'background: #ef2b27; color: #fff; border-radius: 2px; padding: 2px;', msg, rest.length ? rest : '');
  }
};
var getSeconds = function getSeconds(duration) {
  var arr = duration.split(':');
  return +arr[0] * 60 * 60 + +arr[1] * 60 + +arr[2];
};
var rand16 = function rand16() {
  var n = String(Math.round(1000000000000000 + Math.random() * 9000000000000000));

  if (n.length < 16) {
    while (n.length < 16) {
      n += '0';
    }
  }

  if (n.length > 16) {
    n = n.substr(0, 17);
  }

  return n;
};
var uid = function uid() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};
var useDidUpdate = function useDidUpdate(callback, deps) {
  var hasMount = useRef(false);
  useEffect(function () {
    if (hasMount.current) {
      callback();
    } else {
      hasMount.current = true;
    }
  }, deps);
};
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
var getCSIDSectionValue = function getCSIDSectionValue() {
  if (typeof window !== 'undefined' && Buffer) {
    var _typeValueMap$smarttv;

    var ua = new UAParser();
    var device = ua.getDevice();
    var webDevices = ['mobile', 'tablet'];
    var type_value = 'desktop_web';
    var typeValueMap = {
      'smarttv': {
        'Apple': 'ctv_appletv',
        'Amazon': 'ctv_amazonfiretv',
        'LG': 'ctv_lgtv',
        'Vizio': 'ctv_vizio',
        'Roku': 'ctv_roku',
        'Google': 'ctv_androidtv'
      }
    };

    if (webDevices.includes(device.type)) {
      type_value = 'mobile_web';
    } else if (device.type === 'smarttv' && (_typeValueMap$smarttv = typeValueMap['smarttv']) !== null && _typeValueMap$smarttv !== void 0 && _typeValueMap$smarttv[device.vendor]) {
      type_value = typeValueMap['smarttv'][device.vendor];
    }

    return type_value;
  }

  return false;
};

var vcidFallback = localStorage.getItem('vcidFallback') || Math.floor(Math.random() * 1000000 + 1);

if (!localStorage.getItem('vcidFallback')) {
  localStorage.setItem('vcidFallback', vcidFallback);
}

var failedFMSVal = {
  fms_params: {
    _fw_vcid2: vcidFallback
  }
};

var getFMS = function getFMS() {
  return Promise.resolve(_catch(function () {
    return Promise.resolve(window.fmscw.retrieve({
      app: {
        coppa: 0
      }
    }, {
      errorResponseTTL: 3600,
      timeout: 3000
    }));
  }, function () {
    return failedFMSVal;
  }));
};

var getKeywords = function getKeywords(config, provider) {
  var _window, _window$utag_data;

  var keywords = config.keywords || ((_window = window) === null || _window === void 0 ? void 0 : (_window$utag_data = _window.utag_data) === null || _window$utag_data === void 0 ? void 0 : _window$utag_data.keywords);
  var replacedKeywords = keywords.replace('&', 'and').replace(/\s/g, '_');

  if (provider === 'dfp') {
    return replacedKeywords;
  }

  if (provider === 'freewheel') {
    return replacedKeywords.split(',');
  }
};

var createAdUrl = function createAdUrl(config, siteSettings) {
  try {
    var isLive = config.isLive,
        amp = config.amp;
    var adTagParams = {};
    var consentString = null;
    var fmsval = null;
    var gdprApplies = 0;
    var gppData = null;
    var gppSids = null;
    var gppResolvedRef = {
      current: null
    };
    var privacyCookieValue = null;
    var url = null;
    var targetingAllowed = 1;

    if (typeof cbsoptanon !== 'undefined' && cbsoptanon !== null) {
      cbsoptanon.cmd.push(function (cmp) {
        var _cbsoptanon, _cbsoptanon$tcf, _cmp$ot, _cmp$ot2;

        (_cbsoptanon = cbsoptanon) === null || _cbsoptanon === void 0 ? void 0 : (_cbsoptanon$tcf = _cbsoptanon.tcf) === null || _cbsoptanon$tcf === void 0 ? void 0 : _cbsoptanon$tcf.getConsentData(function (consentData, successful) {
          consentString = successful && consentData !== null && consentData !== void 0 && consentData.tcString ? consentData.tcString : null;
          gdprApplies = consentData !== null && consentData !== void 0 && consentData.gdprApplies ? 1 : 0;
        });
        var cookie = cbsoptanon.cookie.getCookie('usprivacy');
        privacyCookieValue = cookie === null || cookie === void 0 ? void 0 : cookie.value;

        if (typeof cmp !== 'undefined' && typeof cmp.gpp !== 'undefined') {
          var _cmp$gpp;

          (_cmp$gpp = cmp.gpp) === null || _cmp$gpp === void 0 ? void 0 : _cmp$gpp.send('ping', function (data) {
            try {
              return Promise.resolve(cmp.gpp.get()).then(function (_cmp$gpp$get) {
                gppData = _cmp$gpp$get;
                gppSids = ((data === null || data === void 0 ? void 0 : data['applicableSections']) || []).join('_');
                gppResolvedRef.current = true;
              });
            } catch (e) {
              return Promise.reject(e);
            }
          });
        } else {
          gppResolvedRef.current = true;
        }

        (_cmp$ot = cmp.ot) === null || _cmp$ot === void 0 ? void 0 : _cmp$ot.targetingAllowed(function (allowed) {
          if (allowed) {
            targetingAllowed = 0;
          }
        });
        (_cmp$ot2 = cmp.ot) === null || _cmp$ot2 === void 0 ? void 0 : _cmp$ot2.addOnConsentChangedHandler(function () {
          var _cmp$ot3;

          (_cmp$ot3 = cmp.ot) === null || _cmp$ot3 === void 0 ? void 0 : _cmp$ot3.targetingAllowed(function (allowed) {
            if (allowed) {
              targetingAllowed = 0;
            }

            return Promise.resolve();
          });
        });
      });
    }

    return Promise.resolve(verifyRefsAreTruthy(50, 100, [gppResolvedRef])).then(function () {
      function _temp3() {
        var _window4, _window4$utag_data, _siteSettings$ad9, _siteSettings$ad10, _siteSettings$ad11, _window5, _window5$utag_data, _window6, _window6$utag_data;

        url = new URL(siteSettings.ad.fwBaseUrl);

        if (!isLive) {
          var _siteSettings$ad, _siteSettings$ad3, _siteSettings$ad6, _siteSettings$ad7, _siteSettings$ad8, _window2, _window2$utag_data, _window3, _window3$location;

          url.searchParams.append('asnw', siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad = siteSettings.ad) === null || _siteSettings$ad === void 0 ? void 0 : _siteSettings$ad.fwNetworkId);
          url.searchParams.append('caid', config === null || config === void 0 ? void 0 : config.video_reference_id);

          if (amp) {
            var _siteSettings$ad2;

            url.searchParams.append('csid', "vcbs_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad2 = siteSettings.ad) === null || _siteSettings$ad2 === void 0 ? void 0 : _siteSettings$ad2.fwBrand) + "_" + getCSIDSectionValue() + "_" + ((config === null || config === void 0 ? void 0 : config.mediaContentType) === 'stream' ? 'live' : 'vod') + "_amp");
          } else if (siteSettings !== null && siteSettings !== void 0 && (_siteSettings$ad3 = siteSettings.ad) !== null && _siteSettings$ad3 !== void 0 && _siteSettings$ad3.ott) {
            var _siteSettings$ad4;

            url.searchParams.append('csid', "vcbs_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad4 = siteSettings.ad) === null || _siteSettings$ad4 === void 0 ? void 0 : _siteSettings$ad4.fwBrand) + "_" + getCSIDSectionValue() + "_vod");
          } else {
            var _siteSettings$ad5;

            url.searchParams.append('csid', "vcbs_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad5 = siteSettings.ad) === null || _siteSettings$ad5 === void 0 ? void 0 : _siteSettings$ad5.fwBrand) + "_" + getCSIDSectionValue() + "_" + ((config === null || config === void 0 ? void 0 : config.mediaContentType) === 'stream' ? 'live' : 'vod'));
          }

          url.searchParams.append('flag', '+amcb+emcr+dtrd+sltp+slcb+vicb+fbad+sync+nucr+aeti');
          url.searchParams.append('metr', 1023);
          url.searchParams.append('nw', siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad6 = siteSettings.ad) === null || _siteSettings$ad6 === void 0 ? void 0 : _siteSettings$ad6.fwNetworkId);
          url.searchParams.append('prof', (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad7 = siteSettings.ad) === null || _siteSettings$ad7 === void 0 ? void 0 : _siteSettings$ad7.fwNetworkId) + ":GoogleDAICSAI_v01");
          url.searchParams.append('pvrn', rand16());
          url.searchParams.append('ssnw', siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad8 = siteSettings.ad) === null || _siteSettings$ad8 === void 0 ? void 0 : _siteSettings$ad8.fwNetworkId);
          url.searchParams.append('resp', 'vmap1+vast4');
          url.searchParams.append('vdur', (config === null || config === void 0 ? void 0 : config.duration) || ((_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$utag_data = _window2.utag_data) === null || _window2$utag_data === void 0 ? void 0 : _window2$utag_data.duration));
          url.searchParams.append('vprn', rand16() + ";_fw_continuous_play=" + ((config === null || config === void 0 ? void 0 : config.continuousPlay) || 0));
          url.searchParams.append('_fw_site_page', (_window3 = window) === null || _window3 === void 0 ? void 0 : (_window3$location = _window3.location) === null || _window3$location === void 0 ? void 0 : _window3$location.href);
        }

        if ((_window4 = window) !== null && _window4 !== void 0 && (_window4$utag_data = _window4.utag_data) !== null && _window4$utag_data !== void 0 && _window4$utag_data.keywords || config !== null && config !== void 0 && config.keywords) {
          var kw = getKeywords(config, 'freewheel');
          url.searchParams.append('_fw_key_words', kw.join(',').toLowerCase());
        }

        if (fmsval && fmsval.fms_params) {
          for (var _i = 0, _Object$entries = Object.entries(fmsval.fms_params); _i < _Object$entries.length; _i++) {
            var _Object$entries$_i = _Object$entries[_i],
                k = _Object$entries$_i[0],
                v = _Object$entries$_i[1];
            var key = !isLive ? k.replace('imafw_', '') : k;
            url.searchParams.append(!isLive ? key.replace('__fw_vcid2', '_fw_vcid2') : key, v);
          }
        }

        if (!isLive) {
          url.searchParams.append('cpPre', 0);
          url.searchParams.append('cpSession', (config === null || config === void 0 ? void 0 : config.continuousPlay) || 0);
          url.searchParams.append('givn', '[GOOGLE_INSTREAM_VIDEO_NONCE]');
        }

        url.searchParams.append('embed', (config === null || config === void 0 ? void 0 : config.embedded) || 0);
        url.searchParams.append('show', siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad9 = siteSettings.ad) === null || _siteSettings$ad9 === void 0 ? void 0 : _siteSettings$ad9.fwBrand);
        url.searchParams.append('site', siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad10 = siteSettings.ad) === null || _siteSettings$ad10 === void 0 ? void 0 : _siteSettings$ad10.site);
        url.searchParams.append('sz', '640x480');
        url.searchParams.append('partner', siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad11 = siteSettings.ad) === null || _siteSettings$ad11 === void 0 ? void 0 : _siteSettings$ad11.fwBrand);
        url.searchParams.append('ftag', (_window5 = window) !== null && _window5 !== void 0 && (_window5$utag_data = _window5.utag_data) !== null && _window5$utag_data !== void 0 && _window5$utag_data.campaign ? window.utag_data.campaign : false);

        if (privacyCookieValue && privacyCookieValue !== '1---') {
          url.searchParams.append(!isLive ? '_fw_us_privacy' : 'imafw__fw_us_privacy', privacyCookieValue);
        }

        url.searchParams.append('gpp', gppData);
        url.searchParams.append('gpp_sid', gppSids);

        if (gdprApplies) {
          url.searchParams.append(!isLive ? '_fw_gdpr' : 'imafw__fw_gdpr', gdprApplies);
          url.searchParams.append(!isLive ? '_fw_gdpr_consent' : 'imafw__fw_gdpr_consent', consentString);
        }

        url.searchParams.append('_fw_is_lat', targetingAllowed);

        if ((_window6 = window) !== null && _window6 !== void 0 && (_window6$utag_data = _window6.utag_data) !== null && _window6$utag_data !== void 0 && _window6$utag_data.keywords || config !== null && config !== void 0 && config.keywords) {
          var _kw = getKeywords(config, 'freewheel');

          for (var _iterator = _createForOfIteratorHelperLoose(_kw), _step; !(_step = _iterator()).done;) {
            var _v = _step.value;
            url.searchParams.append('keywords', _v.toLowerCase());
          }
        }

        if (window.utag_data) {
          var _window7, _window7$utag_data, _window8, _window8$utag_data, _window9, _window9$utag_data;

          if ((_window7 = window) !== null && _window7 !== void 0 && (_window7$utag_data = _window7.utag_data) !== null && _window7$utag_data !== void 0 && _window7$utag_data.pageType) {
            url.searchParams.append('ptype', window.utag_data.pageType);
          }

          if ((_window8 = window) !== null && _window8 !== void 0 && (_window8$utag_data = _window8.utag_data) !== null && _window8$utag_data !== void 0 && _window8$utag_data.pageViewGuid) {
            url.searchParams.append('vguid', window.utag_data.pageViewGuid);
          }

          if ((_window9 = window) !== null && _window9 !== void 0 && (_window9$utag_data = _window9.utag_data) !== null && _window9$utag_data !== void 0 && _window9$utag_data.siteSection) {
            url.searchParams.append('section', window.utag_data.siteSection);
          }
        }

        if (isLive) {
          var _siteSettings$ad12, _window10, _window10$location;

          for (var _iterator2 = _createForOfIteratorHelperLoose(url.searchParams.entries()), _step2; !(_step2 = _iterator2()).done;) {
            var _step2$value = _step2.value,
                _k = _step2$value[0],
                _v2 = _step2$value[1];
            var imafwKey = _k.includes('imafw_') ? _k : "imafw_" + _k;
            adTagParams[imafwKey] = _v2;
          }

          if (targetingAllowed === 1) {
            adTagParams.rdp = 1;
          }

          if (siteSettings !== null && siteSettings !== void 0 && (_siteSettings$ad12 = siteSettings.ad) !== null && _siteSettings$ad12 !== void 0 && _siteSettings$ad12.ott) {
            var _siteSettings$ad13, _siteSettings$ad14, _siteSettings$ad15;

            adTagParams.imafw_csid = "vcbs_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad13 = siteSettings.ad) === null || _siteSettings$ad13 === void 0 ? void 0 : _siteSettings$ad13.fwBrand) + "_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad14 = siteSettings.ad) === null || _siteSettings$ad14 === void 0 ? void 0 : _siteSettings$ad14.platformPrefix) + "_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad15 = siteSettings.ad) === null || _siteSettings$ad15 === void 0 ? void 0 : _siteSettings$ad15.platform) + "_live";
          } else {
            var _siteSettings$ad16;

            adTagParams.imafw_csid = "vcbs_" + (siteSettings === null || siteSettings === void 0 ? void 0 : (_siteSettings$ad16 = siteSettings.ad) === null || _siteSettings$ad16 === void 0 ? void 0 : _siteSettings$ad16.fwBrand) + "_" + (mobileCheck() ? 'mobile' : 'desktop') + "_web_live";
          }

          adTagParams.imafw_host = (_window10 = window) === null || _window10 === void 0 ? void 0 : (_window10$location = _window10.location) === null || _window10$location === void 0 ? void 0 : _window10$location.hostname;
        }

        var _fw_vcid2 = url.searchParams.get('_fw_vcid2');

        if (!_fw_vcid2 && _fw_vcid2 === 'undefined') {
          url.searchParams.set('_fw_vcid2', vcidFallback);
        }

        var finalUrl = url ? url.href.replace(/%3B/g, ';').replace('_fw_continuous_play%3D', '_fw_continuous_play=') : null;
        logMessage("Ad Call URL: " + finalUrl);
        return {
          url: finalUrl,
          adTagParams: adTagParams
        };
      }

      if (!siteSettings) {
        console.error('No Site Settings Provided to CMV Avia, cannot display ads.');
        return false;
      }

      var _temp2 = function () {
        if (config.fms) {
          return Promise.resolve(verifyGlobalVars(100, 10, ['fmscw'])).then(function (isFMSAvailable) {
            var _temp = function () {
              if (isFMSAvailable) {
                return Promise.resolve(getFMS()).then(function (_getFMS) {
                  fmsval = _getFMS;
                });
              } else {
                fmsval = failedFMSVal;
              }
            }();

            if (_temp && _temp.then) return _temp.then(function () {});
          });
        }
      }();

      return _temp2 && _temp2.then ? _temp2.then(_temp3) : _temp3(_temp2);
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

var version = "2.10.2";
var publicDetailsJson = {
	version: version
};

var Player = function Player(_ref) {
  var amp = _ref.amp,
      autoplay = _ref.autoplay,
      captions = _ref.captions,
      capQualityToScreenSize = _ref.capQualityToScreenSize,
      children = _ref.children,
      compactViewBreakpoint = _ref.compactViewBreakpoint,
      continuousPlay = _ref.continuousPlay,
      daiId = _ref.daiId,
      debug = _ref.debug,
      disableTracking = _ref.disableTracking,
      embedded = _ref.embedded,
      enableOm = _ref.enableOm,
      eventCallback = _ref.eventCallback,
      fms = _ref.fms,
      hideAds = _ref.hideAds,
      id = _ref.id,
      isStream = _ref.isStream,
      keywords = _ref.keywords,
      mediaUrl = _ref.mediaUrl,
      metadata = _ref.metadata,
      onContentComplete = _ref.onContentComplete,
      playlistArr = _ref.playlistArr,
      playlistCompleteAction = _ref.playlistCompleteAction,
      playlistIndex = _ref.playlistIndex,
      posterImageUrl = _ref.posterImageUrl,
      setPlaylistIndex = _ref.setPlaylistIndex,
      siteSettings = _ref.siteSettings,
      trayIndex = _ref.trayIndex,
      useManualActiveMode = _ref.useManualActiveMode,
      useReplay = _ref.useReplay,
      useUnmuteCta = _ref.useUnmuteCta,
      vid = _ref.vid;
  var isAnalyticsVerified = useRef(null);
  var firstPlay = useRef(true);
  var initPlaylist = useRef(null);
  var updatedPlaylist = useRef(false);
  var hideAdsRef = useRef(hideAds);
  var mediaUrlRef = useRef(mediaUrl);
  var metadataRef = useRef(metadata);
  var player = useRef(null);
  var playerId = useRef(id || uid());
  var previousTrayIndex = usePrevious({
    trayIndex: trayIndex
  });
  var timer = null;

  var handlePlayerEvent = function handlePlayerEvent(e) {
    var _e$detail2, _e$detail2$error, _e$detail3, _e$detail3$error, _e$detail4, _e$detail4$error, _player$current5, _player$current5$getP;

    var types = PlayerEvent;
    var _e$detail = e.detail,
        interrupted = _e$detail.interrupted,
        adInterrupted = _e$detail.adInterrupted;

    if (eventCallback) {
      eventCallback(e);
    }

    if (interrupted || adInterrupted) {
      if (initPlaylist.current !== null && updatedPlaylist.current) {
        var _player$current, _player$current$getPl, _player$current2, _player$current2$getP;

        updatedPlaylist.current = false;
        player === null || player === void 0 ? void 0 : (_player$current = player.current) === null || _player$current === void 0 ? void 0 : (_player$current$getPl = _player$current.getPlugin) === null || _player$current$getPl === void 0 ? void 0 : _player$current$getPl.call(_player$current, PLAYLIST).clear();
        player === null || player === void 0 ? void 0 : (_player$current2 = player.current) === null || _player$current2 === void 0 ? void 0 : (_player$current2$getP = _player$current2.getPlugin) === null || _player$current2$getP === void 0 ? void 0 : _player$current2$getP.call(_player$current2, PLAYLIST).addResources(initPlaylist.current);
      }
    }

    logMessage("Player event " + e.type);

    switch (e.type) {
      case types.ERROR:
        console.error(e);
        logMessage("Avia has experienced an error with code " + (e === null || e === void 0 ? void 0 : (_e$detail2 = e.detail) === null || _e$detail2 === void 0 ? void 0 : (_e$detail2$error = _e$detail2.error) === null || _e$detail2$error === void 0 ? void 0 : _e$detail2$error.code) + " and message \"" + (e === null || e === void 0 ? void 0 : (_e$detail3 = e.detail) === null || _e$detail3 === void 0 ? void 0 : (_e$detail3$error = _e$detail3.error) === null || _e$detail3$error === void 0 ? void 0 : _e$detail3$error.message) + "\". Player is reset");

        if (e !== null && e !== void 0 && (_e$detail4 = e.detail) !== null && _e$detail4 !== void 0 && (_e$detail4$error = _e$detail4.error) !== null && _e$detail4$error !== void 0 && _e$detail4$error.fatal) {
          try {
            return Promise.resolve(destroyPlayer(playerId.current)).then(function () {
              return Promise.resolve(createPlayer()).then(function () {});
            });
          } catch (e) {
            Promise.reject(e);
          }
        }

        break;

      case types.RESOURCE_END:
        if (e.detail.adInterrupted && getSystemInfo().isIos) {
          logMessage('Avia has detected an ad interruption on iPhone. Player is reset.');

          if (playerId.current) {
            try {
              return Promise.resolve(destroyPlayer(playerId.current)).then(function () {
                return Promise.resolve(createPlayer()).then(function () {});
              });
            } catch (e) {
              Promise.reject(e);
            }
          }
        }

        break;

      case types.RESOURCE_START:
        clearTimeout(timer);
        timer = setTimeout(function () {
          var _player$current3, _player$current3$getP, _player$current3$getP2, _player$current3$getP3, _player$current3$getP4;

          if (player !== null && player !== void 0 && (_player$current3 = player.current) !== null && _player$current3 !== void 0 && (_player$current3$getP = _player$current3.getPlugin) !== null && _player$current3$getP !== void 0 && (_player$current3$getP2 = _player$current3$getP.call(_player$current3, PLAYLIST)) !== null && _player$current3$getP2 !== void 0 && (_player$current3$getP3 = _player$current3$getP2.currentResource) !== null && _player$current3$getP3 !== void 0 && (_player$current3$getP4 = _player$current3$getP3.metadata) !== null && _player$current3$getP4 !== void 0 && _player$current3$getP4.takeCallback) {
            var _player$current4, _player$current4$getP, _player$current4$getP2, _player$current4$getP3, _player$current4$getP4;

            fetch(player === null || player === void 0 ? void 0 : (_player$current4 = player.current) === null || _player$current4 === void 0 ? void 0 : (_player$current4$getP = _player$current4.getPlugin) === null || _player$current4$getP === void 0 ? void 0 : (_player$current4$getP2 = _player$current4$getP.call(_player$current4, PLAYLIST)) === null || _player$current4$getP2 === void 0 ? void 0 : (_player$current4$getP3 = _player$current4$getP2.currentResource) === null || _player$current4$getP3 === void 0 ? void 0 : (_player$current4$getP4 = _player$current4$getP3.metadata) === null || _player$current4$getP4 === void 0 ? void 0 : _player$current4$getP4.takeCallback).then();
          }
        }, 1000);
        break;

      case types.CONTENT_COMPLETE:
        var playerPlaylist = player === null || player === void 0 ? void 0 : (_player$current5 = player.current) === null || _player$current5 === void 0 ? void 0 : (_player$current5$getP = _player$current5.getPlugin) === null || _player$current5$getP === void 0 ? void 0 : _player$current5$getP.call(_player$current5, PLAYLIST);

        if (playerPlaylist !== null && playerPlaylist !== void 0 && playerPlaylist.nextResource && playerPlaylist.currentIndex !== playerPlaylist.length - 1) {
          var _nextPlaylistItem$ad, _nextPlaylistItem$ad$;

          var nextPlaylistItem = _extends({}, playerPlaylist === null || playerPlaylist === void 0 ? void 0 : playerPlaylist.nextResource);

          var nextPlaylistItemAdCallUrl = nextPlaylistItem === null || nextPlaylistItem === void 0 ? void 0 : (_nextPlaylistItem$ad = nextPlaylistItem.ad) === null || _nextPlaylistItem$ad === void 0 ? void 0 : (_nextPlaylistItem$ad$ = _nextPlaylistItem$ad.csai) === null || _nextPlaylistItem$ad$ === void 0 ? void 0 : _nextPlaylistItem$ad$.adCallUrl;
          var nextPlaylistIndex = (playerPlaylist === null || playerPlaylist === void 0 ? void 0 : playerPlaylist.currentIndex) + 1;

          if (nextPlaylistItem && (nextPlaylistItemAdCallUrl === null || nextPlaylistItemAdCallUrl === void 0 ? void 0 : nextPlaylistItemAdCallUrl.indexOf('_fw_continuous_play=0')) > 0 && (nextPlaylistItemAdCallUrl === null || nextPlaylistItemAdCallUrl === void 0 ? void 0 : nextPlaylistItemAdCallUrl.indexOf('cpSession=0')) > 0) {
            nextPlaylistItem.ad.csai.adCallUrl = nextPlaylistItemAdCallUrl.replace('_fw_continuous_play=0', '_fw_continuous_play=1').replace('cpSession=0', 'cpSession=1');
            playerPlaylist.removeResourceAtIndex(nextPlaylistIndex);
            playerPlaylist.addResources(nextPlaylistItem, nextPlaylistIndex);
            updatedPlaylist.current = true;
          }
        }

        if (onContentComplete) {
          onContentComplete();
        }

        break;

      case types.METADATA_CUEPOINT:
        var payload = e.detail.cue;
        logMessage("ID3 Cuepoint: " + payload.data);
        break;
    }
  };

  var listenToPlayerEvents = function listenToPlayerEvents() {
    var events = PlayerEvent;

    if (player.current) {
      Object.keys(events).forEach(function (k) {
        player.current.on(events[k], handlePlayerEvent);
      });
    }
  };

  var unlistenToPlayerEvents = function unlistenToPlayerEvents() {
    var events = PlayerEvent;

    if (player.current) {
      Object.keys(events).forEach(function (k) {
        player.current.off(events[k], handlePlayerEvent);
      });
    }
  };

  var onAmpIntegrationReady = function onAmpIntegrationReady(ampIntegration) {
    if (!player.current) return;
    ampIntegration.method('play', function () {
      player.current.play();
    });
    ampIntegration.method('pause', function () {
      player.current.pause();
    });
    ampIntegration.method('mute', function () {
      player.current.muted = true;
    });
    ampIntegration.method('unmute', function () {
      player.current.muted = false;
    });
    ampIntegration.method('fullscreenenter', function () {
      player.current.enterFullscreen();
    });
    ampIntegration.method('fullscreenexit', function () {
      player.current.exitFullscreen();
    });
    player.current.on('playbackstatechange', function (e) {
      if (e.detail.playbackState == 'paused') {
        ampIntegration.postEvent('pause');
      } else if (e.detail.playbackState == 'playing') {
        ampIntegration.postEvent('playing');
      }
    });
    player.current.on('resourcestart', function () {
      if (player.current.muted) {
        ampIntegration.postEvent('muted');
      }
    });
    player.current.on('mutechange', function (e) {
      if (e.detail.muted) {
        ampIntegration.postEvent('muted');
      } else {
        ampIntegration.postEvent('unmuted');
      }
    });
    player.current.on('streammetadata', function () {
      ampIntegration.postEvent('canplay');
    });
    player.current.on('resourceend', function () {
      ampIntegration.postEvent('ended');
    });
  };

  var prepareAd = function prepareAd(_metadata, continuousPlay) {
    try {
      var _isStream = isStream || typeof (_metadata === null || _metadata === void 0 ? void 0 : _metadata.daiId) === 'string';

      var _daiId = daiId || (_metadata === null || _metadata === void 0 ? void 0 : _metadata.daiId);

      var _vid = vid || (_metadata === null || _metadata === void 0 ? void 0 : _metadata.vid);

      return Promise.resolve(createAdUrl({
        video_reference_id: _metadata === null || _metadata === void 0 ? void 0 : _metadata.videoReferenceId,
        mediaContentType: _isStream ? 'stream' : 'vod',
        continuousPlay: continuousPlay,
        duration: _metadata.duration,
        embedded: embedded,
        fms: fms,
        isLive: _isStream,
        keywords: keywords,
        amp: amp
      }, siteSettings)).then(function (adInfo) {
        return _isStream ? _daiId && _vid ? {
          ssai: {
            assetKey: _daiId,
            enableNonce: true,
            isLive: true,
            adParameters: _extends({
              videoId: _vid
            }, adInfo.adTagParams)
          }
        } : {} : {
          csai: {
            adCallUrl: adInfo.url
          }
        };
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var buildResource = function buildResource(resourceData) {
    try {
      var _temp3 = function _temp3() {
        if (_captions) {
          resourceConfig.location.textTrackUrl = _captions;
        }

        if (_capQualityToScreenSize !== false) {
          resourceConfig.playback.abr.capQualityToScreenSize = true;
        } else {
          resourceConfig.playback.abr.capQualityToScreenSize = false;
        }

        if (takeCallback) {
          resourceConfig.metadata.takeCallback = takeCallback;
        }

        if (_posterImageUrl) {
          resourceConfig.location.posterImageUrl = _posterImageUrl;
        }

        return resourceConfig;
      };

      var _captions = resourceData._captions,
          _capQualityToScreenSize = resourceData._capQualityToScreenSize,
          _continuousPlay = resourceData._continuousPlay,
          _mediaUrl = resourceData._mediaUrl,
          _metadata = resourceData._metadata,
          _hideAds = resourceData._hideAds,
          _posterImageUrl = resourceData._posterImageUrl,
          vidPlaylistPos = resourceData.vidPlaylistPos,
          takeCallback = resourceData.takeCallback;
      var resourceConfig = window.avia.createResourceConfig();
      resourceConfig.location = {
        mediaUrl: _mediaUrl
      };
      resourceConfig.metadata = _extends({
        vidPlaylistPos: vidPlaylistPos
      }, _metadata);

      if (isAnalyticsVerified.current) {
        var _window, _window$utag_data, _window2, _window2$utag_data;

        resourceConfig.metadata = _extends({}, om.getHbParams(resourceConfig.metadata), {
          pageType: (_window = window) === null || _window === void 0 ? void 0 : (_window$utag_data = _window.utag_data) === null || _window$utag_data === void 0 ? void 0 : _window$utag_data.pageType,
          brandPlatformId: (_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$utag_data = _window2.utag_data) === null || _window2$utag_data === void 0 ? void 0 : _window2$utag_data.brandPlatformId
        });
      }

      var _temp4 = function () {
        if (!_hideAds) {
          logMessage('Ads are enabled');
          return Promise.resolve(prepareAd(_metadata, continuousPlay)).then(function (_prepareAd) {
            resourceConfig.ad = _prepareAd;
          });
        }
      }();

      return Promise.resolve(_temp4 && _temp4.then ? _temp4.then(_temp3) : _temp3(_temp4));
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var buildPlaylist = function buildPlaylist() {
    try {
      var _playlist = null;

      var _temp6 = function () {
        if (playlistArr && playlistArr.length) {
          return Promise.resolve(Promise.allSettled(playlistArr.map(function (item, index) {
            try {
              var itemDuration = item.duration ? typeof item.duration === 'string' ? getSeconds(item.duration) : item.duration : 0;
              return Promise.resolve(buildResource({
                _mediaUrl: item.stream_url,
                _captions: item.captions,
                _capQualityToScreenSize: item.capQualityToScreenSize,
                vidPlaylistPos: index.toString(),
                takeCallback: item.take_callback ? item.take_callback : null,
                _posterImageUrl: item.image_url,
                _hideAds: item.hideAds || hideAdsRef.current,
                _metadata: _extends({}, item === null || item === void 0 ? void 0 : item.metadata, {
                  daiId: item.daiId,
                  duration: itemDuration,
                  vid: item.vid,
                  videoReferenceId: item.reference_id,
                  videoTitle: item.title
                })
              })).then(function (_resource) {
                _resource.metadata.assetTitle = item.title;
                _resource.metadata.thumbnail = item.image_url;
                _resource.metadata.duration = itemDuration;
                return _resource;
              });
            } catch (e) {
              return Promise.reject(e);
            }
          }))).then(function (responses) {
            _playlist = responses.map(function (res) {
              return res.value ? res.value : {};
            });
          });
        } else {
          return Promise.resolve(buildResource({
            _captions: captions,
            _capQualityToScreenSize: capQualityToScreenSize,
            _hideAds: hideAdsRef.current,
            _mediaUrl: mediaUrlRef.current,
            _metadata: metadataRef.current,
            _posterImageUrl: posterImageUrl,
            vidPlaylistPos: '0'
          })).then(function (_buildResource) {
            _playlist = _buildResource;
          });
        }
      }();

      return Promise.resolve(_temp6 && _temp6.then ? _temp6.then(function () {
        return _playlist;
      }) : _playlist);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var assignPlaylist = function assignPlaylist() {
    try {
      var _temp9 = function () {
        if (player.current) {
          var playerPlaylist = player.current.getPlugin(PLAYLIST);

          var _temp10 = function () {
            if (playerPlaylist) {
              return Promise.resolve(buildPlaylist()).then(function (_playlist) {
                playerPlaylist.addResources(_playlist);
                initPlaylist.current = playerPlaylist === null || playerPlaylist === void 0 ? void 0 : playerPlaylist.list;
                playerPlaylist.start(playlistIndex);
              });
            } else {
              console.warn('No Playlist Plugin Found');
            }
          }();

          if (_temp10 && _temp10.then) return _temp10.then(function () {});
        } else {
          console.warn('No Active Player Found');
        }
      }();

      return Promise.resolve(_temp9 && _temp9.then ? _temp9.then(function () {}) : void 0);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var assignResource = function assignResource() {
    try {
      return Promise.resolve(buildResource({
        _captions: captions,
        _capQualityToScreenSize: capQualityToScreenSize,
        _mediaUrl: mediaUrlRef.current,
        _metadata: metadataRef.current,
        _hideAds: hideAdsRef.current,
        _posterImageUrl: posterImageUrl,
        vidPlaylistPos: '0'
      })).then(function (resourceConfig) {
        try {
          player.current.attachResource(resourceConfig);
        } catch (error) {
          logMessage('Resource Error');
          console.error(error);
        }
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var handlePlaylistEvent = function handlePlaylistEvent(e) {
    var _e$detail5, _e$detail6, _e$detail7;

    logMessage("Playlist event " + (e === null || e === void 0 ? void 0 : (_e$detail5 = e.detail) === null || _e$detail5 === void 0 ? void 0 : _e$detail5.type));

    if ((e === null || e === void 0 ? void 0 : (_e$detail6 = e.detail) === null || _e$detail6 === void 0 ? void 0 : _e$detail6.id) === 'playlist') {
      var _player$current6, _player$current6$getP;

      var playerPlaylist = player === null || player === void 0 ? void 0 : (_player$current6 = player.current) === null || _player$current6 === void 0 ? void 0 : (_player$current6$getP = _player$current6.getPlugin) === null || _player$current6$getP === void 0 ? void 0 : _player$current6$getP.call(_player$current6, PLAYLIST);

      switch (e === null || e === void 0 ? void 0 : (_e$detail7 = e.detail) === null || _e$detail7 === void 0 ? void 0 : _e$detail7.type) {
        case 'playlistadvanced':
          if (typeof setPlaylistIndex === 'function' && !firstPlay.current) {
            setTimeout(function () {
              var _currentIndex = playerPlaylist._currentIndex;
              setPlaylistIndex(_currentIndex);
            }, 0);
          }

          firstPlay.current = false;
          break;

        case 'playlistcomplete':
          if (typeof playlistCompleteAction === 'function') {
            playlistCompleteAction();
            playerPlaylist.start();
          }

          break;
      }
    }
  };

  var createPlayer = function createPlayer() {
    try {
      var _temp12 = function _temp12(_verifyGlobalVars) {
        var _vtg, _vtg$ts, _vtg$ts$AviaTracking;

        isAnalyticsVerified.current = _verifyGlobalVars;
        logMessage("{PLAYER:" + playerId.current + "} autoplay:" + autoplay);
        avia === null || avia === void 0 ? void 0 : getAutoplayCapabilities().then(function (onfulfilled) {
          logMessage("supportsMutedAutoplay:" + (onfulfilled === null || onfulfilled === void 0 ? void 0 : onfulfilled.supportsMutedAutoplay));
          logMessage("supportsUnmutedAutoplay:" + (onfulfilled === null || onfulfilled === void 0 ? void 0 : onfulfilled.supportsUnmutedAutoplay));
        });
        return Promise.resolve(createVideoPlayer({
          container: "#" + playerId.current,
          id: playerId.current,
          autoplay: autoplay,
          enableOm: enableOm,
          playsInline: true,
          plugins: [hls()].concat(useReplay ? [replay()] : [], [ui({
            activeMode: useManualActiveMode ? 'manual' : 'auto',
            compactViewBreakpoint: compactViewBreakpoint || 240,
            useUnmuteCta: useUnmuteCta
          }), gam({
            debug: debug
          })], playlistArr && playlistArr.length ? [playlist()] : [], isAnalyticsVerified.current ? [(_vtg = vtg) === null || _vtg === void 0 ? void 0 : (_vtg$ts = _vtg.ts) === null || _vtg$ts === void 0 ? void 0 : (_vtg$ts$AviaTracking = _vtg$ts.AviaTracking) === null || _vtg$ts$AviaTracking === void 0 ? void 0 : _vtg$ts$AviaTracking.plugin({
            config: vtg.config
          })] : [])
        })).then(function (_createVideoPlayer) {
          player.current = _createVideoPlayer;
          listenToPlayerEvents();

          if (playlistArr && playlistArr.length) {
            logMessage('Playlist Found');
            player.current.on(PlayerEvent.PLUGIN_EVENT, handlePlaylistEvent);
            assignPlaylist();
          } else {
            logMessage('Single Resource Found');
            assignResource();
          }

          if (!window.activePlayers) {
            window.activePlayers = new Map();
          }

          window.activePlayers.set(playerId.current, player.current);
        });
      };

      return Promise.resolve(disableTracking ? _temp12(!disableTracking && verifyGlobalVars(100, 10, ['utag_data', 'om', 'vtg.ts', 'utag'])) : Promise.resolve(!disableTracking && verifyGlobalVars(100, 10, ['utag_data', 'om', 'vtg.ts', 'utag'])).then(_temp12));
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var destroyPlayer = function destroyPlayer(playerToDestroy) {
    try {
      var _temp15 = function () {
        var _window3, _window3$activePlayer, _window3$activePlayer2;

        if ((_window3 = window) !== null && _window3 !== void 0 && (_window3$activePlayer = _window3.activePlayers) !== null && _window3$activePlayer !== void 0 && (_window3$activePlayer2 = _window3$activePlayer.has) !== null && _window3$activePlayer2 !== void 0 && _window3$activePlayer2.call(_window3$activePlayer, playerToDestroy)) {
          var _temp16 = _finallyRethrows(function () {
            return _catch(function () {
              return Promise.resolve(window.activePlayers.get(playerToDestroy).stop()).then(function () {});
            }, function () {
              logMessage('Failed to stop player prior to destruction');
            });
          }, function (_wasThrown, _result) {
            unlistenToPlayerEvents();
            window.activePlayers["delete"](playerToDestroy);
            return Promise.resolve(removeVideoPlayer(playerToDestroy)).then(function () {
              if (_wasThrown) throw _result;
              return _result;
            });
          });

          if (_temp16 && _temp16.then) return _temp16.then(function () {});
        }
      }();

      return Promise.resolve(_temp15 && _temp15.then ? _temp15.then(function () {}) : void 0);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  useEffect(function () {
    var initialize = function initialize() {
      try {
        var _temp19 = function _temp19() {
          if (amp) {
            (window.AmpVideoIframe = window.AmpVideoIframe || []).push(onAmpIntegrationReady);
          }

          return function () {
            if (playlistArr && playlistArr.length) {
              player.current.off(PlayerEvent.PluginEvent, handlePlaylistEvent);
            }

            destroyPlayer(playerId.current);
          };
        };

        logMessage('Initialize player');
        window.avia = avia;
        window.cmv_avia = publicDetailsJson;

        var _temp20 = function () {
          var _window4, _window4$activePlayer, _window4$activePlayer2;

          if (!player.current && !((_window4 = window) !== null && _window4 !== void 0 && (_window4$activePlayer = _window4.activePlayers) !== null && _window4$activePlayer !== void 0 && (_window4$activePlayer2 = _window4$activePlayer.has) !== null && _window4$activePlayer2 !== void 0 && _window4$activePlayer2.call(_window4$activePlayer, playerId.current))) {
            return Promise.resolve(createPlayer()).then(function () {});
          }
        }();

        return Promise.resolve(_temp20 && _temp20.then ? _temp20.then(_temp19) : _temp19(_temp20));
      } catch (e) {
        return Promise.reject(e);
      }
    };

    initialize();
  }, []);
  useDidUpdate(function () {
    logMessage('Single resource updated');
    hideAdsRef.current = hideAds;
    mediaUrlRef.current = mediaUrl;
    metadataRef.current = metadata;
    assignResource();
  }, [mediaUrl]);
  useDidUpdate(function () {
    var _player$current7, _player$current7$getP;

    var playerPlaylist = player === null || player === void 0 ? void 0 : (_player$current7 = player.current) === null || _player$current7 === void 0 ? void 0 : (_player$current7$getP = _player$current7.getPlugin) === null || _player$current7$getP === void 0 ? void 0 : _player$current7$getP.call(_player$current7, PLAYLIST);

    if (typeof playlistIndex !== 'undefined' && playerPlaylist) {
      if (typeof trayIndex !== 'undefined' && trayIndex !== previousTrayIndex.trayIndex) {
        playerPlaylist.clear();
        playerPlaylist.currentIndex = playlistIndex;
        assignPlaylist();
      } else {
        playerPlaylist.currentIndex = playlistIndex;
      }
    }
  }, [playlistIndex, trayIndex]);
  useDidUpdate(function () {
    var _player$current8, _player$current8$getP;

    var playerPlaylist = player === null || player === void 0 ? void 0 : (_player$current8 = player.current) === null || _player$current8 === void 0 ? void 0 : (_player$current8$getP = _player$current8.getPlugin) === null || _player$current8$getP === void 0 ? void 0 : _player$current8$getP.call(_player$current8, PLAYLIST);

    if (typeof playlistIndex !== 'undefined' && playerPlaylist && trayIndex === previousTrayIndex.trayIndex) {
      playerPlaylist.clear();
      playerPlaylist.currentIndex = playlistIndex;
      assignPlaylist();
    }
  }, [playlistArr]);
  return /*#__PURE__*/React.createElement("div", {
    className: "avia"
  }, /*#__PURE__*/React.createElement("div", {
    className: "avia__player"
  }, /*#__PURE__*/React.createElement("div", {
    id: playerId.current
  }, children)));
};

var UpNext = function UpNext(_ref) {
  var _player$getPlugin;

  var player = _ref.player;
  var noticeShown = useRef(false);
  var getNextAttempted = useRef(false);
  var displayAtTimeRemaining = 10;
  var displayDuration = 6000;
  var defaultThumb = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  var _useState = useState(null),
      nextItem = _useState[0],
      setNextItem = _useState[1];

  var _useState2 = useState(false),
      isNextItemShown = _useState2[0],
      setIsNextItemShown = _useState2[1];

  var playerPlaylist = player === null || player === void 0 ? void 0 : (_player$getPlugin = player.getPlugin) === null || _player$getPlugin === void 0 ? void 0 : _player$getPlugin.call(player, PLAYLIST);

  var handlePanelClick = function handlePanelClick() {
    noticeShown.current = false;
    getNextAttempted.current = false;
    setIsNextItemShown(false);

    if (playerPlaylist.nextResource) {
      playerPlaylist.next();
    } else {
      playerPlaylist.currentIndex = 0;
      playerPlaylist.start();
    }
  };

  useEffect(function () {
    var events = PlayerEvent;

    var convertSeconds = function convertSeconds(seconds) {
      var mm = Math.floor(seconds / 60);
      var ss = seconds - mm * 60;
      return mm + ":" + (ss > 9 ? ss : "'0'" + ss);
    };

    var trunc = function trunc(str, limit) {
      if (!str || str.length <= limit) return str;
      return str.substr(0, limit) + "...";
    };

    var getNext = function getNext() {
      if (!playerPlaylist || getNextAttempted.current) return;
      var next = playerPlaylist.nextResource;
      getNextAttempted.current = true;

      if (!next) {
        next = playerPlaylist.list[0];
      }

      setNextItem({
        title: next.metadata.assetTitle ? trunc(next.metadata.assetTitle, 75) : 'Video',
        thumb: next.metadata.thumbnail || next.location.thumbnailTrackUrl || defaultThumb,
        duration: next.metadata.duration ? convertSeconds(next.metadata.duration) : null
      });
    };

    var hideNext = function hideNext() {
      getNextAttempted.current = false;
      setIsNextItemShown(false);
    };

    var showNext = function showNext(endSlate) {
      noticeShown.current = true;
      setIsNextItemShown(true);
      setTimeout(function () {
        hideNext();

        if (!endSlate) {
          noticeShown.current = false;
        }
      }, displayDuration);
    };

    var canDisplay = function canDisplay() {
      return player.dimensions.width >= 432;
    };

    var checkTime = function checkTime(contentTime, contentDuration) {
      var show = false;

      if (Math.round(contentDuration) - Math.round(contentTime) < displayAtTimeRemaining) {
        show = true;
      }

      if (show && canDisplay()) {
        showNext(true);
      } else if (show) {
        noticeShown.current = true;
      }
    };

    var handlePlayerEvent = function handlePlayerEvent(e) {
      switch (e.type) {
        case events.CONTENT_START:
          getNext();
          showNext();
          break;

        case events.CONTENT_PROGRESS:
          if (!getNextAttempted.current) {
            getNext();
          }

          if (!noticeShown.current && nextItem) {
            checkTime(e.detail.contentTime, e.detail.contentDuration);
          }

          break;

        case events.PRESENTATION_SIZE_CHANGE:
        case events.RESOURCE_INTERRUPTED:
        case events.CONTENT_COMPLETE:
          console.log('CONTENT COMPLETE');
          hideNext();
          break;
      }
    };

    var listenToPlayerEvents = function listenToPlayerEvents() {
      if (player) {
        Object.keys(events).forEach(function (k) {
          player.on(events[k], handlePlayerEvent);
        });
      }
    };

    var unlistenToPlayerEvents = function unlistenToPlayerEvents() {
      if (player) {
        Object.keys(events).forEach(function (k) {
          player.off(events[k], handlePlayerEvent);
        });
      }
    };

    listenToPlayerEvents();
    return function () {
      try {
        unlistenToPlayerEvents();
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    };
  }, [player, nextItem]);
  return !playerPlaylist ? null : /*#__PURE__*/React.createElement("avia-ui", null, /*#__PURE__*/React.createElement("div", {
    slot: "overlay"
  }, isNextItemShown && /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-panel",
    onClick: handlePanelClick,
    role: "button",
    tabIndex: "0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-wrapper"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-heading"
  }, "Up Next")), /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-block"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-title"
  }, nextItem === null || nextItem === void 0 ? void 0 : nextItem.title)), /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-thumb"
  }, /*#__PURE__*/React.createElement("img", {
    src: nextItem === null || nextItem === void 0 ? void 0 : nextItem.thumb,
    alt: nextItem === null || nextItem === void 0 ? void 0 : nextItem.title
  }), (nextItem === null || nextItem === void 0 ? void 0 : nextItem.duration) && /*#__PURE__*/React.createElement("div", {
    className: "cvui-up-next-duration"
  }, nextItem.duration)))))));
};

export { Player, UpNext };
//# sourceMappingURL=index.modern.js.map
